"use client";

import { type FC, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { openDocumentInSidebar } from "@/lib/threadDocumentOps";
import { getLiveThreadId } from "@/components/assistant";

/**
 * Inline card for a Praetor case-document revision produced by `save_revision`.
 * Clicking opens the live Praetor document PDF in the sidebar via
 * `/api/praetor/documents/{id}`. No download menu — the document lives in PraetorIS.
 */
export const PraetorRevisionBlock: FC<{ praetorDocumentId: string; title?: string }> = ({
  praetorDocumentId,
  title,
}) => {
  const { t } = useTranslation();

  const displayTitle = title || t("praetorRevision.untitled");

  const handleClick = useCallback(() => {
    void openDocumentInSidebar(praetorDocumentId, undefined, getLiveThreadId());
  }, [praetorDocumentId]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        void openDocumentInSidebar(praetorDocumentId, undefined, getLiveThreadId());
      }
    },
    [praetorDocumentId],
  );

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      className="my-4 flex items-center gap-3 rounded-lg border border-border px-4 py-3 cursor-pointer hover:bg-muted/30 transition-colors"
    >
      <img src="/docx-icon.png" alt="DOCX" className="size-13 p-0.5 shrink-0" />
      <span className="flex-1 font-body text-sm font-medium truncate">{displayTitle}</span>
      <span className="text-[10px] text-muted-foreground shrink-0">
        {t("praetorRevision.badge")}
      </span>
    </div>
  );
};
